import { Route } from "../../../utils/models/Route";
import type { StageDestination } from "./stage-destinations";

/**
 * Splitting one day into two ("Split route here", or adding a day) and
 * merging two days into one (removing a day). Everything after the change
 * moves along a day so the days stay numbered in order. Pure, so it can be
 * tested and snapshotted for undo.
 */

export interface DayKeyedState {
  /** Day of every route, by route key. */
  routeDays: { [routeKey: string]: number | undefined };
  dayInfo: { [day: number]: any };
  stageDestinations: { [day: number]: StageDestination };
}

function shiftKeys<T>(byDay: { [day: number]: T }, fromDay: number, by: number): { [day: number]: T } {
  const shifted: { [day: number]: T } = {};
  for (const [key, value] of Object.entries(byDay || {})) {
    const day = Number(key);
    shifted[day >= fromDay ? day + by : day] = value;
  }
  return shifted;
}

/**
 * The day-keyed state after splitting `splitRouteKey` on day `splitDay`.
 *  - Later days move up one; so do main-line routes after the split route on
 *    the same day (they now follow the split point). Offshoots stay put.
 *  - Day `splitDay`'s end now belongs to the new day, so its destination
 *    moves with it.
 */
export function splitIntoDays(
  current: DayKeyedState,
  routes: { [routeKey: string]: Route },
  splitRouteKey: string,
  splitDay: number
): DayKeyedState {
  const splitRoute = routes[splitRouteKey];
  const routeDays: DayKeyedState['routeDays'] = {};
  for (const [key, route] of Object.entries(routes)) {
    const day = current.routeDays[key] ?? route.day ?? 1;
    const isMain = (route.route_style ?? 'route') === 'route';
    const followsSplitSameDay = day === splitDay && key !== splitRouteKey && isMain &&
      !!splitRoute && route.position > splitRoute.position;
    routeDays[key] = day > splitDay || followsSplitSameDay ? day + 1 : day;
  }

  return {
    routeDays,
    dayInfo: shiftKeys(current.dayInfo, splitDay + 1, 1),
    stageDestinations: shiftKeys(current.stageDestinations, splitDay, 1),
  };
}

/**
 * The day-keyed state after day `mergedDay + 1` is folded into day
 * `mergedDay`: its routes join day `mergedDay`, later days move down one, and
 * the stop that used to separate the two days goes.
 */
export function mergeDays(current: DayKeyedState, routes: { [routeKey: string]: Route }, mergedDay: number): DayKeyedState {
  const routeDays: DayKeyedState['routeDays'] = {};
  for (const [key, route] of Object.entries(routes)) {
    const day = current.routeDays[key] ?? route.day ?? 1;
    routeDays[key] = day > mergedDay ? day - 1 : day;
  }

  const dayInfo = { ...(current.dayInfo || {}) };
  delete dayInfo[mergedDay + 1];
  const stageDestinations = { ...(current.stageDestinations || {}) };
  delete stageDestinations[mergedDay];

  return {
    routeDays,
    dayInfo: shiftKeys(dayInfo, mergedDay + 2, -1),
    stageDestinations: shiftKeys(stageDestinations, mergedDay + 1, -1),
  };
}

/** The current day-keyed state, as a snapshot (deep copy). */
export function captureDayKeyedState(state: {
  routes: { [routeKey: string]: Route };
  dayInfo?: any;
  stageDestinations?: any;
}): DayKeyedState {
  const routeDays: DayKeyedState['routeDays'] = {};
  for (const [key, route] of Object.entries(state.routes)) routeDays[key] = route.day;
  return JSON.parse(JSON.stringify({
    routeDays,
    dayInfo: state.dayInfo || {},
    stageDestinations: state.stageDestinations || {},
  }));
}

/** Writes a day-keyed snapshot back onto planner state. */
export function applyDayKeyedState(state: any, snapshot: DayKeyedState): void {
  const copy: DayKeyedState = JSON.parse(JSON.stringify(snapshot));
  for (const [key, day] of Object.entries(copy.routeDays)) {
    if (state.routes[key]) state.routes[key].day = day;
  }
  state.dayInfo = copy.dayInfo;
  state.stageDestinations = copy.stageDestinations;
}
